<script setup lang="ts">
import { computed, nextTick, onMounted, ref, shallowRef, useTemplateRef, watch } from 'vue'
import { PageMeta } from '@/@vxapp/@Enums/PageMeta'
import { AccessPageMeta } from '@/access/accessPageMeta'
import { canPageMeta } from '@/@layouts/plugins/casl'
import { PaginationField } from '@/@vxapp/@Enums/PaginationField'
import { ResponseCode } from '@/@vxapp/@Enums/PHP/HTTP/ResponseCode'
import { createLogger } from '@/@vxapp/@core/Logger/VxLog'
import { useJobsStore } from '@/@vxapp/Jobs/useJobsStore'
import NewJobDialog from '@/components/Dialogs/Jobs/NewJobDialog.vue'
import ProjectListToolbar from '@/components/ServiceLists/Projects/ProjectListToolbar.vue'
import type {
  ProjectListQueryParams,
  ProjectPaginationMeta,
} from '@/components/ServiceLists/Projects/projectListTypes'
import ProjectsDataTable from '@/components/Tables/Projects/ProjectsDataTable.vue'

type ManagedQueryKey = 'page' | 'limit' | 'sort' | 'search' | 'hideInactive'

definePage({ meta: { ...PageMeta.Projects.list.meta, pageTitle: PageMeta.Projects.list.title } })

const log = createLogger('ProjectsListPage')
const route = useRoute()
const router = useRouter()
const jobsStore = useJobsStore() as any

const projectsDataTable =
  useTemplateRef<InstanceType<typeof ProjectsDataTable>>('projectsDataTable')

const isNewJobDialogVisible = shallowRef(false)
const isRouteQueryReady = shallowRef(false)
const resultCount = ref(0)

const defaultQueryParams: ProjectListQueryParams = {
  page: PaginationField.QueryParams.page,
  limit: PaginationField.QueryParams.limit,
  sort: '-id',
  search: undefined,
  hideInactive: true,
}

const allowedLimits = [10, 25, 50, 100]

const managedQueryKeys: ManagedQueryKey[] = ['page', 'limit', 'sort', 'search', 'hideInactive']

function normalizeQueryValue(value: unknown) {
  const normalizedValue = Array.isArray(value) ? value[0] : value

  return typeof normalizedValue === 'string' || typeof normalizedValue === 'number'
    ? String(normalizedValue)
    : undefined
}

function normalizeQueryParams(source: Record<string, unknown>): ProjectListQueryParams {
  const page = Number.parseInt(normalizeQueryValue(source.page) ?? '', 10)
  const limit = Number.parseInt(normalizeQueryValue(source.limit) ?? '', 10)
  const sort = normalizeQueryValue(source.sort)
  const search = normalizeQueryValue(source.search)?.trim()
  const hideInactive = normalizeQueryValue(source.hideInactive)

  return {
    page: Number.isInteger(page) && page > 0 ? page : defaultQueryParams.page,
    limit: allowedLimits.includes(limit) ? limit : defaultQueryParams.limit,
    sort: sort === '+id' || sort === '-id' ? sort : defaultQueryParams.sort,
    search: search || undefined,
    hideInactive: hideInactive !== 'false',
  }
}

const queryParams = computed(() =>
  normalizeQueryParams(jobsStore.queryParams ?? defaultQueryParams)
)

function queryParamsMatch(left: ProjectListQueryParams, right: ProjectListQueryParams) {
  return managedQueryKeys.every(key => left[key] === right[key])
}

function applyRouteQuery() {
  const routeQueryParams = normalizeQueryParams(route.query as Record<string, unknown>)

  if (queryParamsMatch(queryParams.value, routeQueryParams)) return false

  jobsStore.queryParams = { ...jobsStore.queryParams, ...routeQueryParams }

  return true
}

function buildRouteQuery() {
  const nextQuery = { ...route.query } as Record<string, unknown>

  for (const key of managedQueryKeys) delete nextQuery[key]

  return {
    ...nextQuery,
    ...(queryParams.value.page !== defaultQueryParams.page
      ? { page: String(queryParams.value.page) }
      : {}),
    ...(queryParams.value.limit !== defaultQueryParams.limit
      ? { limit: String(queryParams.value.limit) }
      : {}),
    ...(queryParams.value.sort !== defaultQueryParams.sort ? { sort: queryParams.value.sort } : {}),
    ...(queryParams.value.search ? { search: queryParams.value.search } : {}),
    ...(queryParams.value.hideInactive !== defaultQueryParams.hideInactive
      ? { hideInactive: String(queryParams.value.hideInactive) }
      : {}),
  }
}

async function syncRouteQuery() {
  const nextQuery = buildRouteQuery()

  if (JSON.stringify(route.query) !== JSON.stringify(nextQuery)) {
    await router.replace({ query: nextQuery })
  }
}

async function fetchProjects() {
  await projectsDataTable.value?.fetchJobs()
}

async function updateQueryParams(patch: Partial<ProjectListQueryParams>) {
  jobsStore.queryParams = { ...queryParams.value, ...patch }
  if (isRouteQueryReady.value) await syncRouteQuery()
  await nextTick()
  await fetchProjects()
}

async function handleCreateProject() {
  if (!canPageMeta(AccessPageMeta.Projects.Add.Meta)) return
  try {
    const { responseCode } = await jobsStore.fetchTemplate()

    if (responseCode === ResponseCode.OK) {
      isNewJobDialogVisible.value = true

      return
    }

    log.error('Failed to load the project template', { responseCode })
  } catch (error) {
    log.error('Failed to load project template', error)
  }
}

async function handleCreateProjectSubmit(jobData: Record<string, unknown>) {
  if (!canPageMeta(AccessPageMeta.Projects.Add.Meta)) return
  try {
    const { responseCode } = await jobsStore.create(jobData, false)

    if (responseCode === ResponseCode.CREATED) {
      await fetchProjects()

      return
    }

    log.error('Failed to create the project', { responseCode })
  } catch (error) {
    log.error('Failed to create project', error)
  }
}

function handleLoaded(pagination: ProjectPaginationMeta) {
  resultCount.value = pagination.entriesFound
}

async function handleClear() {
  await updateQueryParams({ search: undefined, page: 1 })
}

async function handleInactiveChange(hideInactive: boolean) {
  await updateQueryParams({ hideInactive, page: 1 })
}

async function handleLimitChange(limit: number) {
  await updateQueryParams({
    limit: allowedLimits.includes(limit) ? limit : defaultQueryParams.limit,
    page: 1,
  })
}

async function handleSearch(search: string) {
  await updateQueryParams({ search: search || undefined, page: 1 })
}

async function handleSortChange(sort: string) {
  await updateQueryParams({ sort, page: 1 })
}

async function handlePageChange(page: number) {
  await updateQueryParams({ page })
}

onMounted(async () => {
  jobsStore.queryParams = { ...defaultQueryParams }
  applyRouteQuery()
  isRouteQueryReady.value = true
  await nextTick()
  await fetchProjects()
})

watch(
  () => route.query,
  async () => {
    if (!isRouteQueryReady.value || !applyRouteQuery()) return
    await nextTick()
    await fetchProjects()
  },
  { deep: true }
)
</script>

<template>
  <main class="projects-workspace">
    <section class="projects-workspace__hero">
      <div>
        <span class="projects-workspace__eyebrow">PROJECT EXECUTION / LIVE</span>
        <h1>Work moves here.</h1>
        <p>
          Scan project identity, ownership, status, and commercial routing in a focused execution
          queue.
        </p>
      </div>

      <div class="projects-workspace__hero-actions">
        <div class="projects-workspace__metric">
          <span>QUEUE SIZE</span>
          <strong>{{ resultCount.toLocaleString() }}</strong>
        </div>
        <VBtn
          v-if="canPageMeta(AccessPageMeta.Projects.Add.Meta)"
          color="primary"
          prepend-icon="tabler-plus"
          size="large"
          @click="handleCreateProject"
        >
          New project
        </VBtn>
      </div>
    </section>

    <ProjectListToolbar
      :is-loading="jobsStore.isLoading"
      :query-params="queryParams"
      :result-count="resultCount"
      @clear="handleClear"
      @inactive-change="handleInactiveChange"
      @limit-change="handleLimitChange"
      @refresh="fetchProjects"
      @search="handleSearch"
      @sort-change="handleSortChange"
    />

    <ProjectsDataTable
      ref="projectsDataTable"
      :query-params="queryParams"
      @loaded="handleLoaded"
      @page-change="handlePageChange"
    />
  </main>

  <NewJobDialog
    v-if="isNewJobDialogVisible"
    v-model:is-dialog-visible="isNewJobDialogVisible"
    :job-data="jobsStore.template"
    @submit="handleCreateProjectSubmit"
  />
</template>

<style scoped lang="scss">
.projects-workspace {
  display: grid;
  min-inline-size: 0;
  gap: 1rem;
  font-feature-settings: 'cv01', 'ss03';
}

.projects-workspace__hero {
  display: grid;
  overflow: hidden;
  min-inline-size: 0;
  align-items: end;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 22px;
  background:
    radial-gradient(circle at 82% 14%, rgba(var(--v-theme-primary), 0.18), transparent 30%),
    linear-gradient(145deg, rgba(var(--v-theme-on-surface), 0.025), transparent),
    rgb(var(--v-theme-surface));
  gap: 2rem;
  grid-template-columns: minmax(0, 1fr) auto;
  padding: clamp(1.5rem, 4vw, 3.5rem);
}

.projects-workspace__eyebrow,
.projects-workspace__metric span {
  color: rgb(var(--v-theme-primary));
  font-family: monospace;
  font-size: 0.6875rem;
}

.projects-workspace h1 {
  margin: 0.65rem 0 0;
  color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
  font-size: clamp(2.35rem, 6vw, 4.5rem);
  font-weight: 510;
  letter-spacing: -0.04em;
  line-height: 1;
}

.projects-workspace__hero p {
  max-inline-size: 41rem;
  margin: 1rem 0 0;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  line-height: 1.6;
}

.projects-workspace__hero-actions {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.projects-workspace__metric {
  display: grid;
  gap: 0.2rem;
  text-align: end;
}

.projects-workspace__metric strong {
  font-family: monospace;
  font-size: 1.7rem;
  font-weight: 510;
}

.projects-workspace__hero-actions :deep(.v-btn) {
  min-block-size: 3rem;
  border-radius: 6px;
}

@media (max-width: 767px) {
  .projects-workspace__hero {
    align-items: start;
    border-radius: 12px;
    grid-template-columns: minmax(0, 1fr);
  }

  .projects-workspace__hero-actions {
    justify-content: space-between;
  }

  .projects-workspace__metric {
    text-align: start;
  }
}

@media (max-width: 424px) {
  .projects-workspace__hero-actions {
    align-items: stretch;
    flex-direction: column;
  }

  .projects-workspace__hero-actions :deep(.v-btn) {
    inline-size: 100%;
  }
}
</style>
